import axios from "axios";

const ADMIN_API_BASE_URL = "/ofc/api/v1/admin/calendar-events";
const PUBLIC_API_BASE_URL = "/ofc/api/v1/calendar-events";

// Configure axios defaults
axios.defaults.withCredentials = true;

export interface CompanyCalendarEventDTO {
  id?: number;
  title: string;
  description?: string;
  startDate: string;
  endDate: string;
  color?: string;
  isActive?: boolean;
  createdByName?: string;
  createdAt?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error?: string;
}

class CompanyCalendarEventsApiService {
  /**
   * Get all company calendar events (Admin only)
   */
  async getAllEvents(): Promise<CompanyCalendarEventDTO[]> {
    try {
      const response =
        await axios.get<ApiResponse<CompanyCalendarEventDTO[]>>(
          ADMIN_API_BASE_URL,
        );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to fetch company calendar events",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error ||
            "Failed to fetch company calendar events",
        );
      }
      throw error;
    }
  }

  /**
   * Get all active company calendar events (Public)
   */
  async getAllActiveEvents(): Promise<CompanyCalendarEventDTO[]> {
    try {
      const response =
        await axios.get<ApiResponse<CompanyCalendarEventDTO[]>>(
          PUBLIC_API_BASE_URL,
        );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error ||
            "Failed to fetch active company calendar events",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error ||
            "Failed to fetch active company calendar events",
        );
      }
      throw error;
    }
  }

  /**
   * Get company calendar events by date range (Public)
   */
  async getEventsByDateRange(
    startDate: string,
    endDate: string,
  ): Promise<CompanyCalendarEventDTO[]> {
    try {
      const response = await axios.get<ApiResponse<CompanyCalendarEventDTO[]>>(
        `${PUBLIC_API_BASE_URL}/range`,
        {
          params: { startDate, endDate },
        },
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to fetch company calendar events",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error ||
            "Failed to fetch company calendar events",
        );
      }
      throw error;
    }
  }

  /**
   * Get a specific company calendar event by ID (Admin only)
   */
  async getEventById(id: number): Promise<CompanyCalendarEventDTO> {
    try {
      const response = await axios.get<ApiResponse<CompanyCalendarEventDTO>>(
        `${ADMIN_API_BASE_URL}/${id}`,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to fetch company calendar event",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error ||
            "Failed to fetch company calendar event",
        );
      }
      throw error;
    }
  }

  /**
   * Create a new company calendar event (Admin only)
   */
  async createEvent(
    event: CompanyCalendarEventDTO,
  ): Promise<CompanyCalendarEventDTO> {
    try {
      const response = await axios.post<ApiResponse<CompanyCalendarEventDTO>>(
        ADMIN_API_BASE_URL,
        event,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to create company calendar event",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error ||
            "Failed to create company calendar event",
        );
      }
      throw error;
    }
  }

  /**
   * Update an existing company calendar event (Admin only)
   */
  async updateEvent(
    id: number,
    event: CompanyCalendarEventDTO,
  ): Promise<CompanyCalendarEventDTO> {
    try {
      const response = await axios.put<ApiResponse<CompanyCalendarEventDTO>>(
        `${ADMIN_API_BASE_URL}/${id}`,
        event,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to update company calendar event",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error ||
            "Failed to update company calendar event",
        );
      }
      throw error;
    }
  }

  /**
   * Delete a company calendar event (Admin only)
   */
  async deleteEvent(id: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<void>>(
        `${ADMIN_API_BASE_URL}/${id}`,
      );
      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to delete company calendar event",
        );
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error ||
            "Failed to delete company calendar event",
        );
      }
      throw error;
    }
  }
}

const companyCalendarEventsApiService = new CompanyCalendarEventsApiService();
export default companyCalendarEventsApiService;
